import type { PlayerStore } from '../../state/player_store.js';
import { SubscribersList } from '../../state/subscribers_list.js';
import type { Size } from '../../types.js';
import { throttle } from '../../utils/throttle.js';
import type {
  WaveformWorker,
  WorkerMessage,
} from './processors/waveform_worker.js';
import { WaveformLoader } from './waveform_loader.js';

const WAVE_LINE_WIDTH = 1;
const WAVE_GAP_WIDTH = 0;

const LINE_GAP = WAVE_LINE_WIDTH + WAVE_GAP_WIDTH;
const CHANNEL_PADDING = 10;
const LABEL_WIDTH = 40;
const LABEL_FONT = '600 14px Inter';
const LABEL_COLOR = '#999999';
const CHANNEL_LABELS = ['L', 'R', 'C', 'Lfe', 'Ls', 'Rs', 'Lrs', 'Rrs'];
const CANVAS_PADDING = 8;

const BACKGROUND_COLOR = '#242424';
const PROGRESS_BACKGROUND_COLOR = '#292929';
const SEGMENT_BACKGROUND_COLOR = '#191919';
const SEGMENT_LINE_COLOR = '#707070';
const WAVEFORM_COLOR = '#999999';
const WAVEFORM_PROGRESS_COLOR = '#DBDBDB';
const WAVEFORM_SEGMENT_COLOR = '#3BE098';
const WAVEFORM_OUTSIDE_SEGMENT_COLOR = '#424242';
const WAVEFORM_PROGRESS_SEGMENT_COLOR = '#5FFEB2';
const PROGRESS_LINE_COLOR = '#ffffff';

export class WaveformCanvas {
  #canvasElement: HTMLCanvasElement | undefined;
  #canvasContext: CanvasRenderingContext2D | null;

  #channelsData: Float32Array[] = [];
  #totalSamples: number = 0;
  #processedSamples: number = 0;
  #paddedChannelsData: Float32Array[] = [];
  #channelsMaxData: Float32Array[] = [];

  #width: number = 300;
  #height: number = 300;
  #drawLines: number = 0;

  #progress: number = 0;

  #subscribersList = new SubscribersList();
  #store: PlayerStore;

  #redrawLabels: boolean = true;
  #isDrawing: boolean = false;

  #worker: WaveformWorker;
  #loader: WaveformLoader;

  constructor(container: HTMLElement, store: PlayerStore) {
    this.#canvasElement = document.createElement('canvas');
    this.#canvasElement.style.display = 'block';
    this.#canvasElement.style.width = '100%';
    this.#canvasElement.style.height = '100%';
    this.#canvasElement.style.padding = `${CANVAS_PADDING}px`;
    this.#store = store;
    this.#subscribersList.subscribe(
      store.size.$containerSize,
      this.#handleContainerSizeChange,
    );
    this.#subscribersList.listen(this.#store.timeline.$progress, this.draw);
    this.#subscribersList.listen(
      this.#store.segment.$currentSegment,
      this.draw,
    );
    container.append(this.#canvasElement);
    this.#canvasContext = this.#canvasElement.getContext('2d');

    this.#worker = new Worker(
      new URL('./processors/waveform_worker.ts', import.meta.url),
      { type: 'module' },
    );
    this.#worker.onmessage = this.#handleWorkerMessage;

    this.#canvasElement.addEventListener('click', this.#handleCanvasClick);

    this.#loader = new WaveformLoader(
      container,
      (channelsData, totalSamples) =>
        this.updateChannelsData(channelsData, totalSamples),
      store.playableMedia.$duration,
    );
  }

  draw = () => {
    if (this.#isDrawing) {
      return;
    }

    this.#isDrawing = true;

    requestAnimationFrame(() => {
      if (!this.#canvasContext || !this.#canvasElement) {
        this.#isDrawing = false;
        return;
      }

      this.#progress =
        (this.#width - LABEL_WIDTH) *
          (this.#store.timeline.$progress.get() ?? 0) +
        LABEL_WIDTH;

      const dpr = Math.ceil(globalThis.devicePixelRatio);

      if (
        this.#width * dpr !== this.#canvasElement.width ||
        this.#height * dpr !== this.#canvasElement.height
      ) {
        this.#canvasElement.width = this.#width * dpr;
        this.#canvasElement.height = this.#height * dpr;
        this.#canvasContext.scale(dpr, dpr);
        this.#redrawLabels = true;
      }
      const clearX = this.#redrawLabels ? 0 : LABEL_WIDTH - 1; // Pixel rounding caused the start of the line to not be cleared properly
      this.#canvasContext.clearRect(clearX, 0, this.#width, this.#height);
      const numChannels = this.#channelsData.length;

      this.#canvasContext.save();

      for (let channel = 0; channel < numChannels; channel++) {
        this.#drawChannel(channel, numChannels);
      }
      this.#canvasContext.restore();
      this.#redrawLabels = false;
      this.#isDrawing = false;
    });
  };

  #drawChannel = (channel: number, numChannels: number) => {
    const channelHeight =
      (this.#height - numChannels * CHANNEL_PADDING) / numChannels;
    const yOffset = channel * (channelHeight + CHANNEL_PADDING);

    this.#drawBackground(
      LABEL_WIDTH,
      yOffset,
      this.#width - LABEL_WIDTH,
      channelHeight,
      8,
    );
    if (this.#redrawLabels) {
      this.#drawChannelLabel(channel, yOffset, channelHeight);
    }
    this.#drawChannelWaveform(channel, yOffset, channelHeight);
    this.#drawProgressLine(yOffset, channelHeight);
  };

  #drawBackground = (
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    if (!this.#canvasContext) {
      return;
    }
    const segmentStart = this.#getCurrentSegmentStart();
    const hasSegment = segmentStart !== undefined;
    const segmentStartX = segmentStart ?? LABEL_WIDTH;
    const segmentEndX = this.#getCurrentSegmentEnd() ?? this.#width;

    this.#canvasContext.save();

    this.#createRoundedRectPath(x, y, width, height, radius);
    this.#canvasContext.clip();

    this.#canvasContext.fillStyle = BACKGROUND_COLOR;
    this.#canvasContext.fillRect(x, y, width, height);
    this.#canvasContext.fillStyle = PROGRESS_BACKGROUND_COLOR;
    this.#canvasContext.fillRect(x, y, this.#progress - x, height);
    if (hasSegment) {
      this.#canvasContext.fillStyle = SEGMENT_BACKGROUND_COLOR;
      this.#canvasContext.fillRect(x, y, segmentStartX - x, height);
      this.#canvasContext.fillRect(
        segmentEndX,
        y,
        x + width - segmentEndX,
        height,
      );
      this.#canvasContext.fillStyle = SEGMENT_LINE_COLOR;
      this.#canvasContext.fillRect(segmentStartX, y, 1, height);
      this.#canvasContext.fillRect(segmentEndX, y, 1, height);
    }
    this.#canvasContext.restore();
  };

  #createRoundedRectPath = (
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    if (!this.#canvasContext) {
      return;
    }
    this.#canvasContext.beginPath();
    this.#canvasContext.moveTo(x + radius, y);
    this.#canvasContext.arcTo(x + width, y, x + width, y + height, radius);
    this.#canvasContext.arcTo(x + width, y + height, x, y + height, radius);
    this.#canvasContext.arcTo(x, y + height, x, y, radius);
    this.#canvasContext.arcTo(x, y, x + width, y, radius);
    this.#canvasContext.closePath();
  };

  #drawChannelLabel = (
    channel: number,
    yOffset: number,
    channelHeight: number,
  ) => {
    if (!this.#canvasContext) {
      return;
    }
    this.#canvasContext.fillStyle = LABEL_COLOR;
    this.#canvasContext.font = LABEL_FONT;
    const labelText = CHANNEL_LABELS[channel] ?? '';
    this.#canvasContext.fillText(labelText, 5, yOffset + channelHeight / 2 + 5);
  };

  #drawChannelWaveform = (
    channel: number,
    yOffset: number,
    channelHeight: number,
  ) => {
    if (!this.#canvasContext) {
      return;
    }
    const ctx = this.#canvasContext;

    const clipWaveform = (startX: number, endX: number, color: string) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(startX, -channelHeight / 2, endX - startX, channelHeight);
      ctx.clip();
      ctx.fillStyle = color;
      drawWaveform(startX, endX);
      ctx.restore();
    };

    const drawWaveform = (startX: number, endX: number) => {
      const startLine = Math.floor(startX / LINE_GAP);
      const endLine = Math.ceil(endX / LINE_GAP);
      const maxData = this.#channelsMaxData[channel];
      if (!maxData) {
        return;
      }
      for (let i = startLine; i <= endLine; i += 1) {
        const lineValue = maxData[i];
        const x = i * LINE_GAP;
        const y = (lineValue * channelHeight) / 2;

        ctx.fillRect(x, -y, WAVE_LINE_WIDTH, y * 2);
      }
    };

    ctx.save();
    ctx.translate(LABEL_WIDTH, yOffset + channelHeight / 2);

    const segmentStart = this.#getCurrentSegmentStart();
    if (segmentStart === undefined) {
      ctx.fillStyle = WAVEFORM_COLOR;
      const progressX = this.#progress - LABEL_WIDTH;
      drawWaveform(progressX, this.#drawLines * LINE_GAP);
      clipWaveform(0, progressX, WAVEFORM_PROGRESS_COLOR);
    }

    const segmentEnd = this.#getCurrentSegmentEnd();
    if (segmentStart !== undefined && segmentEnd !== undefined) {
      clipWaveform(
        segmentStart - LABEL_WIDTH,
        segmentEnd - LABEL_WIDTH,
        WAVEFORM_SEGMENT_COLOR,
      );
      clipWaveform(
        0,
        segmentStart - LABEL_WIDTH,
        WAVEFORM_OUTSIDE_SEGMENT_COLOR,
      );
      clipWaveform(
        segmentEnd - LABEL_WIDTH,
        this.#drawLines * LINE_GAP,
        WAVEFORM_OUTSIDE_SEGMENT_COLOR,
      );

      clipWaveform(
        segmentStart - LABEL_WIDTH,
        this.#progress - LABEL_WIDTH,
        WAVEFORM_PROGRESS_SEGMENT_COLOR,
      );
    }

    ctx.restore();
  };

  #drawProgressLine = (yOffset: number, channelHeight: number) => {
    if (this.#progress <= LABEL_WIDTH || !this.#canvasContext) {
      return;
    }
    this.#canvasContext.strokeStyle = PROGRESS_LINE_COLOR;
    this.#canvasContext.lineWidth = 1;
    this.#canvasContext.beginPath();
    this.#canvasContext.moveTo(this.#progress, yOffset);
    this.#canvasContext.lineTo(this.#progress, yOffset + channelHeight);
    this.#canvasContext.stroke();
  };

  updateChannelsData(channelsData: Float32Array[], totalSamples: number) {
    this.#totalSamples = Math.min(totalSamples, 90000);

    // Calculate what portion of the total audio this chunk represents
    const fractionProcessed = channelsData[0].length / totalSamples;

    // Calculate exact (fractional) target
    const exactTarget = this.#totalSamples * fractionProcessed;

    // Round based on running total to avoid accumulating errors
    const targetSamples =
      Math.round(this.#processedSamples + exactTarget) - this.#processedSamples;
    this.#processedSamples += targetSamples;

    // Send the data for pre-processing to decrease the memory footprint and time needed for #updateChannelsMaxData()
    this.#worker.postMessage({
      channelsData,
      samples: Math.min(channelsData[0].length, targetSamples),
      preProcess: true,
    });
  }

  #updateChannelsMaxData() {
    this.#drawLines = (this.#width - LABEL_WIDTH) / LINE_GAP;
    if (this.#channelsData.length === 0) {
      return;
    }
    this.#worker.postMessage({
      channelsData: this.#paddedChannelsData,
      samples: this.#drawLines,
    });
    this.#redrawLabels = true;
  }

  setUrl(url: string) {
    this.#loader.setUrl(url);
  }

  destroy() {
    this.#canvasElement?.removeEventListener('click', this.#handleCanvasClick);
    this.#canvasElement?.remove();
    this.#canvasElement = undefined;
    this.#subscribersList.unsubscribeAll();
    this.#worker.terminate();
    this.#loader.destroy();
  }

  #getCurrentSegmentStart() {
    const range = this.#store.segment.$currentSegment.get().range;
    if (!range) {
      return undefined;
    }
    return (
      this.#store.segment.$currentSegmentStartProgress.get() *
        (this.#width - LABEL_WIDTH) +
      LABEL_WIDTH
    );
  }

  #getCurrentSegmentEnd() {
    const range = this.#store.segment.$currentSegment.get().range;
    if (!range) {
      return undefined;
    }
    return (
      this.#store.segment.$currentSegmentEndProgress.get() *
        (this.#width - LABEL_WIDTH) +
      LABEL_WIDTH
    );
  }

  #handleContainerSizeChange = throttle(
    (size: Size) => {
      if (!this.#canvasContext || !this.#canvasElement) {
        return;
      }
      this.#width = size.width - CANVAS_PADDING * 2;
      this.#height = size.height - CANVAS_PADDING * 2;
      this.#store.size.setNaturalSize(size);
      this.#updateChannelsMaxData();
    },
    50,
    true,
  );

  #handleCanvasClick = (event: MouseEvent) => {
    if (!this.#canvasElement) {
      return;
    }

    const rect = this.#canvasElement.getBoundingClientRect();
    const x = event.clientX - rect.left - CANVAS_PADDING;
    const progress = (x - LABEL_WIDTH) / (this.#width - LABEL_WIDTH);

    if (progress >= 0 && progress <= 1) {
      this.#store.seek(progress);
    }
  };

  #handleWorkerMessage = (event: MessageEvent<WorkerMessage>) => {
    const { channelsMaxData, preProcess } = event.data;
    if (channelsMaxData.length === 0) {
      return;
    }
    if (preProcess) {
      if (this.#channelsData.length === 0) {
        this.#channelsData = channelsMaxData;
        this.#paddedChannelsData = channelsMaxData.map((channel) => {
          const paddedArray = new Float32Array(
            Math.max(this.#totalSamples, channel.length),
          );
          paddedArray.set(channel);
          return paddedArray;
        });
      } else {
        for (let i = 0; i < channelsMaxData.length; i++) {
          const newArray = new Float32Array(
            this.#channelsData[i].length + channelsMaxData[i].length,
          );
          newArray.set(this.#channelsData[i]);
          newArray.set(channelsMaxData[i], this.#channelsData[i].length);
          const paddedLength = Math.max(this.#totalSamples, newArray.length);
          const paddedArray = new Float32Array(paddedLength);
          paddedArray.set(newArray.subarray(0, paddedLength));
          this.#channelsData[i] = newArray;
          this.#paddedChannelsData[i] = paddedArray;
        }
      }
      this.#updateChannelsMaxData();
      return;
    }

    this.#channelsMaxData = channelsMaxData;
    this.#loader.hideLoader();
    this.draw();
  };
}
